import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  TextField,
} from "@mui/material";
import WizardContentPreview from "./WizardContentPreview";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import {
  setLayerArray,
  setContentLayers,
  setLayerMedias,
  setTextLayers,
  setBPLRTComponents,
  setGroupedAssetList,
  setContentResolution,
  setCanvasRes,
} from "../../../../store/actions/cmsAction";
import { openActionDialog } from "../../../global/ActionConfirmationDialog";
import { inputOnlyNumbers } from "../../../../helpers/inputProcessing";
import { ResolutionType } from "../../../../helpers/resolution";
import { TypographyNormal, TypographySmall } from "../../../customComponent";
import update from "immutability-helper";
import { v4 as uuidv4 } from "uuid";
import parser from "html-react-parser";

const FormatWizard = ({
  open,
  handleClose,
  setResType,
  handleUpdateInput,
  editFlag,
  startFlagWizard,
  handleBack,
}) => {
  const dispatch = useDispatch();
  const { contentRes, contentLayers } = useSelector((state) => state.cms);

  const { t } = useTranslation("cms");
  const { t: tCommon } = useTranslation();

  const [startFlag, setStartFlag] = startFlagWizard;
  const [wizardConfig, setWizardConfig] = useState({
    width: 1080,
    height: 1920,
    layerCount: 2,
  });

  useEffect(() => {
    if (open) {
      let initWidth = 1080;
      let initHeight = 1920;
      let initLayerCount = 2;
      if (contentRes.width !== 0 && contentRes.height !== 0) {
        initWidth = contentRes.width;
        initHeight = contentRes.height;
      }

      const currentLayerCount = Object.keys(contentLayers).length;
      if (currentLayerCount > 0) {
        initLayerCount = currentLayerCount;
      }

      setWizardConfig({
        width: initWidth,
        height: initHeight,
        layerCount: initLayerCount,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleChangeWidth = (event) => {
    const value = event.target.value;
    const updated = {
      ...wizardConfig,
      width: value,
    };
    setWizardConfig(updated);
  };

  const handleChangeHeight = (event) => {
    const value = event.target.value;
    const updated = {
      ...wizardConfig,
      height: value,
    };
    setWizardConfig(updated);
  };

  const handleChangeLayerCount = (event) => {
    const value = event.target.value;
    const updated = {
      ...wizardConfig,
      layerCount: value,
    };
    setWizardConfig(updated);
  };

  const handleSwapValues = () => {
    const { width, height, layerCount } = wizardConfig;
    const updated = {
      width: height,
      height: width,
      layerCount,
    };
    setWizardConfig(updated);
  };

  const calculateLayoutGrid = (config) => {
    const { width, height, layerCount } = config;
    if (layerCount <= 0) return { rows: 0, cols: 0, cssGridStyle: {} };

    const N = Math.ceil(Math.sqrt(layerCount));
    const cols = N;
    const rows = Math.ceil(layerCount / cols);

    const totalCells = rows * cols;
    const emptyCells = totalCells - layerCount;
    const cssGridStyle = {
      display: "grid",
      gap: "4px",
    };

    let lastLayerStyle = {};
    if (width >= height) {
      cssGridStyle["gridTemplateColumns"] = `repeat(${cols}, 1fr)`;
      cssGridStyle["gridTemplateRows"] = `repeat(${rows}, 1fr)`;

      if (emptyCells > 0) {
        const lastLayerColIndex = (layerCount - 1) % cols;
        const columnSpanStart = lastLayerColIndex + 1;

        lastLayerStyle = {
          gridColumn: `${columnSpanStart} / ${cols + 1}`,
        };
      }
    } else {
      cssGridStyle["gridTemplateRows"] = `repeat(${cols}, 1fr)`;
      cssGridStyle["gridTemplateColumns"] = `repeat(${rows}, 1fr)`;

      if (emptyCells > 0) {
        const lastLayerRowIndex = (layerCount - 1) % rows;
        const rowSpanStart = lastLayerRowIndex + 1;

        lastLayerStyle = {
          gridColumn: `${rowSpanStart} / ${rows + 1}`,
        };
      }
    }

    return { rows, cols, cssGridStyle, lastLayerStyle };
  };

  const generateInitialLayers = (resetFlag = true, currentLayerIDs = []) => {
    const { width, height, layerCount } = wizardConfig;

    // Use the helper to get grid configuration
    const { rows, cols } = calculateLayoutGrid(wizardConfig);

    let targetRows = rows;
    let targetCols = cols;
    if (width < height) {
      targetRows = cols;
      targetCols = rows;
    }

    // Calculate individual layer dimensions for a uniform cell
    const layerWidth = width / targetCols;
    const layerHeight = height / targetRows;

    const setupLayers = {};
    const emptyCells = targetRows * targetCols - layerCount;

    for (let i = 0; i < layerCount; i++) {
      const isLastLayer = i === layerCount - 1;

      // Determine the row and column index (0-based) for the standard grid
      const colIndex = i % targetCols;
      const rowIndex = Math.floor(i / targetCols);

      let currentLayerWidth = layerWidth;
      let currentLayerHeight = layerHeight;

      // Position (top-left corner) remains based on uniform grid position
      const x = colIndex * layerWidth;
      const y = rowIndex * layerHeight;

      // --- Stretch the Last Layer ---
      if (isLastLayer && emptyCells > 0) {
        // The last layer needs to span accordingly to the end of the content area.
        const cellsToSpan = targetCols - colIndex; // The number of cells from the last layer's start position to the end of the row.
        currentLayerWidth = layerWidth * cellsToSpan;
      }

      let newID = uuidv4();
      if (!resetFlag) {
        newID = currentLayerIDs[i] ?? uuidv4();
      }

      const newLayer = {
        id: newID,
        name: "",
        type: "media",
        active: false,
        dragFlag: true,
        transition: "NONE",
        bgColor: "unset",
        dimensions: {
          left: x,
          top: y,
          width: currentLayerWidth,
          height: currentLayerHeight,
          zIndex: i + 1,
          lockRatio: false,
        },
      };

      setupLayers[newID] = newLayer;
    }

    return setupLayers;
  };

  const handleOpenActionDialog = (action, cancel) => {
    openActionDialog(dispatch, action, cancel);
  };

  const processChanges = () => {
    const { layerCount } = wizardConfig;
    const currentLayers = Object.keys(contentLayers);

    let resetLayers = false;
    if (currentLayers.length > layerCount) {
      dispatch(setLayerMedias({ reset: true }));
      resetLayers = true;
    }

    const setupLayers = generateInitialLayers(resetLayers, currentLayers);
    const setupLayerArray = Object.keys(setupLayers);
    dispatch(setLayerArray(setupLayerArray));

    const input = wizardConfig;
    input.width = parseInt(input.width);
    input.height = parseInt(input.height);

    const { width, height } = input;
    let resLabel = `${width} x ${height}`;
    const orientation = width < height ? "P" : "L";
    let resChosen = ResolutionType[orientation][resLabel];
    if (!resChosen) {
      resLabel = `custom0`;
      resChosen = ResolutionType[orientation][resLabel];
    }
    setResType(resLabel);

    const setupContentRes = {
      width: width,
      height: height,
      orientation: orientation,
      label: resLabel,
      custom: resChosen.custom,
      free: resChosen.free,
    };
    dispatch(setContentResolution(setupContentRes));
    handleUpdateInput({
      width: setupContentRes.width,
      height: setupContentRes.height,
      orientation: orientation,
    });

    const setupCanvasRes = {
      width: width < height ? 360 : 640,
      height: width < height ? 640 : 360,
      orientation: orientation,
    };
    if (setupContentRes.free === true) {
      setupCanvasRes.width = width;
      setupCanvasRes.height = height;
    }
    dispatch(setCanvasRes(setupCanvasRes));

    let widthRatio = 1;
    let heightRatio = 1;
    if (!setupContentRes.free) {
      widthRatio = setupContentRes.width / setupCanvasRes.width;
      heightRatio = setupContentRes.height / setupCanvasRes.height;
    }

    let copyLayers = resetLayers ? { ...setupLayers } : { ...contentLayers };
    setupLayerArray.forEach((layerID) => {
      const layerDim = setupLayers[layerID].dimensions;
      const adjustedLeft = layerDim.left / widthRatio;
      const adjustedTop = layerDim.top / heightRatio;
      const adjusted = {
        left: adjustedLeft,
        top: adjustedTop,
        width: layerDim.width,
        height: layerDim.height,
      };
      if (!copyLayers[layerID]) {
        copyLayers[layerID] = setupLayers[layerID];
      }
      copyLayers = update(copyLayers, {
        [layerID]: { dimensions: { $merge: adjusted } },
      });
    });

    dispatch(setContentLayers(copyLayers));
    dispatch(setTextLayers({}));
    dispatch(setBPLRTComponents({}));
    dispatch(setGroupedAssetList({ reset: true }));
    setStartFlag(false);
  };

  const handleSubmitWizard = () => {
    if (editFlag) {
      handleOpenActionDialog(() => {
        processChanges();
        handleClose();
      });
    } else {
      processChanges();
      handleClose();
    }
  };

  return (
    <>
      <Dialog maxWidth="md" fullWidth open={open}>
        <DialogTitle
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          {!editFlag ? t("formatWizard.title") : t("formatWizard.editTitle")}
        </DialogTitle>

        <DialogContent>
          <Box display={"flex"} width={"100%"}>
            <Box width={"50%"}>
              <Box display={"flex"}>
                <Box>
                  <Box display={"flex"} gap={2} alignItems={"center"}>
                    <TypographyNormal
                      width={"125px"}
                      sx={{ ml: 1, whiteSpace: "pre" }}
                    >
                      {t("formatWizard.widthLabel")}
                    </TypographyNormal>

                    <Box>
                      <TextField
                        size="small"
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        required
                        value={wizardConfig.width}
                        onChange={handleChangeWidth}
                        onKeyDown={(e) => inputOnlyNumbers(e)}
                        inputProps={{
                          style: { textAlign: "center" },
                        }}
                        InputProps={{ sx: { width: 100 } }}
                        sx={{ alignItems: "center" }}
                      />
                    </Box>
                  </Box>

                  <Box display={"flex"} gap={2} alignItems={"center"}>
                    <TypographyNormal
                      width={"125px"}
                      sx={{ ml: 1, whiteSpace: "pre" }}
                    >
                      {t("formatWizard.heightLabel")}
                    </TypographyNormal>

                    <Box>
                      <TextField
                        size="small"
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        required
                        value={wizardConfig.height}
                        onChange={handleChangeHeight}
                        onKeyDown={(e) => inputOnlyNumbers(e)}
                        inputProps={{
                          style: { textAlign: "center" },
                        }}
                        InputProps={{ sx: { width: 100 } }}
                        sx={{ alignItems: "center" }}
                      />
                    </Box>
                  </Box>

                  <Box display={"flex"} gap={2} alignItems={"center"}>
                    <TypographyNormal
                      width={"125px"}
                      sx={{ ml: 1, whiteSpace: "pre" }}
                    >
                      {t("formatWizard.numberOfLayerLabel")}
                    </TypographyNormal>

                    <Box>
                      <TextField
                        size="small"
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        required
                        value={wizardConfig.layerCount}
                        onChange={handleChangeLayerCount}
                        onKeyDown={(e) => inputOnlyNumbers(e)}
                        inputProps={{
                          style: { textAlign: "center" },
                          maxLength: 1,
                        }}
                        InputProps={{ sx: { width: 50 } }}
                        sx={{ alignItems: "center" }}
                      />
                    </Box>
                  </Box>
                </Box>

                <Box display={"flex"} alignItems={"center"}>
                  <Tooltip
                    placement="top"
                    disableInteractive
                    title={t("formatWizard.switchDimBtn")}
                  >
                    <Button
                      variant="outlined"
                      color="info"
                      onClick={handleSwapValues}
                      sx={{
                        p: 0,
                        ml: 1,
                        mb: 6,
                        minWidth: "33px",
                        height: "55px",
                        boxShadow: 2,
                      }}
                    >
                      <AutorenewIcon fontSize="small" />
                    </Button>
                  </Tooltip>
                </Box>
              </Box>

              {!startFlag && (
                <Box display={"flex"} maxWidth={"300px"} sx={{ mt: 2, ml: 1 }}>
                  <TypographySmall>
                    {parser(t("formatWizard.hintText"))}
                  </TypographySmall>
                </Box>
              )}
            </Box>

            <Box width={"50%"}>
              <WizardContentPreview
                config={wizardConfig}
                calculateLayoutGrid={calculateLayoutGrid}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Box>
            <Box display={"flex"} gap={2}>
              <Button
                variant="outlined"
                fullWidth
                onClick={!startFlag ? handleClose : handleBack}
              >
                {!startFlag ? tCommon("cancelBtn") : tCommon("backBtn")}
              </Button>

              <Button
                variant="contained"
                fullWidth
                onClick={handleSubmitWizard}
              >
                {!editFlag ? tCommon("createBtn") : tCommon("submitBtn")}
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FormatWizard;
